import * as React from 'react';
import { Textbox } from '../inputs/textbox';
import { PrimaryButton } from '../buttons/primary-button';
import { IAutocompleteResults } from '../../DTO/search';
import { Autocomplete } from './autocomplete';

interface IProps {
    id: string;
    onSearch: (val: string) => void;
    onChange: (val: string, setAutocompleteResults?: (hits: string[]) => void) => void;
    searchButtonText?: string;
    placeHolder?: string;
    charsForAutocomplete?: number;
    searchText: string;
    autoCompleteResults?: IAutocompleteResults;
    setActiveHit?: (index: number) => void;
    setAutoCompleteActive?: (value: boolean) => void;
}

interface IState {
    searchText: string;
    focusSearchTextbox: boolean;
}

export class SearchBar extends React.Component<IProps, IState> {
    static defaultProps = {
        autoCompleteResults: null,
        charsForAutocomplete: 1,
        searchButtonText: "Search",
        placeHolder: ""
    }

    constructor(props) {
        super(props);

        this.state = {
            searchText: this.props.searchText,
            focusSearchTextbox: false,
        }

        this.HasAutocomplete = this.props.autoCompleteResults != null;
    }

    HasAutocomplete: boolean;

    /// <summary>
    /// Intercepts special keys that handle autocomplete scrolling and selection
    /// </summary>
    onInputKeyDown = (e: React.KeyboardEvent) => {
        const { autoCompleteResults, setActiveHit } = this.props;

        switch (e.keyCode) {
            //Up arrow
            //Move selected autocomplete result up one
            case 38: {
                e.preventDefault();
                let newHit = 0;
                if (autoCompleteResults.activeHit > -1) {
                    // move up
                    newHit = autoCompleteResults.activeHit - 1;
                    setActiveHit(newHit);

                    if (newHit <= -1) {
                        this.setState({ focusSearchTextbox: true })
                    }
                    else {
                        this.setState({ focusSearchTextbox: false });
                    }
                }

                break;
            }

            //Down arrow
            //Move selected autocomplete result down one
            case 40: {
                e.preventDefault();
                if (autoCompleteResults.activeHit < autoCompleteResults.hits.length) {
                    setActiveHit(autoCompleteResults.activeHit + 1);
                }

                break;
            }

            //Enter key
            //If an autocomplete option is selected, change the search text to that and submit
            case 13: {
                let term = this.state.searchText;

                if (autoCompleteResults.activeHit > -1) {
                    term = this.props.autoCompleteResults.hits[this.props.autoCompleteResults.activeHit].query;
                    this.setSearchText(term);
                    this.props.setActiveHit(-1);                    
                }

                this.props.setAutoCompleteActive(false);
                this.props.onSearch(term);
                break;
            }
            // Tab
            // Set text to current selection
            case 9:
                let term = this.state.searchText;
                if (autoCompleteResults.activeHit > -1) {
                    term = this.props.autoCompleteResults.hits[this.props.autoCompleteResults.activeHit].query;
                    this.setSearchText(term);
                    this.props.setActiveHit(-1);
                }
                this.props.setAutoCompleteActive(false);
                break;
            // Esc
            case 27:
                if (!this.HasAutocomplete) {
                    return;
                }

                if (this.props.autoCompleteResults.activeHit == -1) {
                    this.props.setAutoCompleteActive(false);
                }
                break;
        }
    }

    setSearchText = (searchText: string) => {
        this.setState({ searchText });
    }

    onInputChange(value: string) {
        this.setSearchText(value);
        this.props.onChange(value);
    }

    AutocompleteResultClicked = (value: string) => {
        this.setSearchText(value);
        this.props.setActiveHit(-1);
        this.props.setAutoCompleteActive(false);
        this.props.onSearch(value);
    }

    onFocus = () => {
        if (!this.HasAutocomplete) {
            return;
        }

        if (this.state.searchText.length >= this.props.charsForAutocomplete) {
            this.props.setAutoCompleteActive(true);
        }
    }

    handleSearchButtonClick = () => {
        this.props.onSearch(this.state.searchText);
    }

    clearSearchInput = () => {
        this.setState({
            searchText: '',
            focusSearchTextbox: true
        });
    }

    renderAutocomplete = () => {
        const { autoCompleteResults } = this.props;

        if (!autoCompleteResults.isActive || this.state.searchText.length < 3 || autoCompleteResults.hits.length === 0) {
            return;
        }

        return (
            <Autocomplete
                autocompleteResults={this.props.autoCompleteResults}
                searchTerm={this.state.searchText}
                onResultClick={this.AutocompleteResultClicked}
                onResultActive={this.props.setActiveHit}
            />
        );
    }

    render() {
        return (
            <>
                <div className="autocomplete-input-container inline-form-input" onKeyDown={this.onInputKeyDown}>
                    <Textbox
                        id={this.props.id}
                        onInput={(e) => { this.onInputChange(e.currentTarget.value) }}
                        placeHolder={this.props.placeHolder}
                        setFocus={this.state.focusSearchTextbox}
                        onFocus={this.onFocus}
                        value={this.state.searchText}
                        labelText={"Search"}
                        cssClass={"inline-form-input autocomplete-input"}
                        labelCssClass={"visually-hidden"}
                    />
                    <button type="button" className="x-btn off-white inline-form-clear" onClick={this.clearSearchInput}>
                        <span className="visually-hidden">Clear input</span>
                    </button>
                    {this.renderAutocomplete()}
                </div>

                <button className="square-btn blue inline-form-submit" type='submit' onClick={this.handleSearchButtonClick}>
                    <svg className="svg-icon" aria-hidden="true" focusable="false">
                        <use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref="#icon-search"></use>
                    </svg>
                    <span className="visually-hidden">{this.props.searchButtonText}</span>
                </button>
            </>
        )
    }
}